import { useEffect, useRef, useState } from 'react';
import { FilterItem, RemoveFilter } from './types';

export const parseFilters = (filters: FilterItem[]) =>
  filters.filter((filter) =>
    Array.isArray(filter.value) ? !!filter.value.length : !!filter.value
  );

export const useFiltersState = (
  initialFilters: FilterItem[] | (() => FilterItem[]),
  onChange: (filters: FilterItem[]) => void
) => {
  const [filtersState, setFiltersState] = useState<FilterItem[]>(initialFilters);

  // Always the latest filters, including changes not rendered yet, so consecutive updates build on
  // each other instead of on a stale render.
  const filtersStateRef = useRef(filtersState);

  useEffect(() => {
    filtersStateRef.current = filtersState;
  }, [filtersState]);

  const appliedFilters = parseFilters(filtersState);

  const commitFilters = (nextFilters: FilterItem[]) => {
    filtersStateRef.current = nextFilters;
    setFiltersState(nextFilters);
  };

  const removeFilter: RemoveFilter = (name, nextValue) => {
    const nextFilters = filtersStateRef.current.map((filter) => {
      if (filter.name !== name) return filter;

      const value = typeof nextValue === 'function' ? nextValue(filter.value) : nextValue;

      return { ...filter, value: value ?? null };
    });

    commitFilters(nextFilters);
    onChange(parseFilters(nextFilters));
  };

  const updateFilters = (filters: FilterItem[]) => {
    const nextFilters = filtersStateRef.current.map((filter) => {
      const updatedFilter = filters.find((f) => f.name === filter.name);

      if (!updatedFilter) {
        return filter;
      }

      return {
        ...filter,
        ...updatedFilter,
        group: {
          ...filter.group,
          ...updatedFilter.group,
        },
      };
    });

    commitFilters(nextFilters);
    // Emitted in the drawer's order so the query string keeps its existing parameter order.
    onChange(parseFilters(filters));
  };

  const clearAll = () => {
    const nextFilters = filtersStateRef.current.map((filter) => ({ ...filter, value: null }));

    commitFilters(nextFilters);
    onChange(parseFilters(nextFilters));
  };

  return {
    filtersState,
    setFiltersState,
    appliedFilters,
    removeFilter,
    updateFilters,
    clearAll,
  };
};
